<template>
  <Teleport to="body">
    <!-- The backdrop does not dismiss: a stray click outside would throw away
         edited hours and an unsaved note. Escape and the close button do. -->
    <div v-if="open" class="tc-cell-detail-backdrop">
      <div
        class="tc-attribution-card tc-cell-detail"
        role="dialog"
        aria-label="Cell detail"
        data-testid="cell-detail-modal"
        @click.stop
      >
        <div class="tc-attribution-header tc-cell-detail-header">
          <div class="tc-cell-detail-title">
            <span class="tc-cell-detail-day">{{ dayLabel }}</span>
            <span class="tc-cell-detail-hours-badge">{{ hours.toFixed(2) }}h</span>
          </div>
          <button
            type="button"
            class="tc-btn-icon tc-cell-detail-close"
            aria-label="Close"
            data-testid="cell-detail-close"
            @click="emit('close')"
          >
            <svg viewBox="0 0 18 18" fill="none" aria-hidden="true" class="tc-cell-picker-x">
              <path d="M13.5 4.5L4.5 13.5M4.5 4.5l9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="tc-attribution-body tc-scroll">
          <!-- Confidence cue — plain words for the amber corner flag the user
               clicked; the "why" (reasoning + less-sure) is revealed below.
               Clears once the row is confirmed. -->
          <div
            v-if="showConfidenceCue"
            class="tc-cell-detail-confidence"
            data-testid="cell-detail-confidence"
          >Low confidence</div>

          <!-- Project — the shared combobox field (same list as the resolver:
               search + Recent + memory ranking) -->
          <div class="tc-attribution-field" :class="{ 'border border-danger rounded p-2': flagged('project') }" data-testid="cell-detail-project-field">
            <label class="tc-attribution-label">Project</label>
            <SearchCombobox
              testid="cell-detail-project"
              placeholder="Search projects…"
              :model-value="projectModel"
              :options="projectResults"
              :loading="projectLoading"
              :empty-label="projectEmpty"
              :disabled="regenerating"
              @update:query="setProjectQuery"
              @select="onProjectSelect"
            />
          </div>

          <!-- Phase / Task — editable, cell-scoped (edit standard rule 3):
               a change moves this cell's events only. On-sheet narrowing +
               VP fallback via the shared cascade. -->
          <template v-if="showPhase">
            <div class="tc-attribution-field" :class="{ 'border border-danger rounded p-2': flagged('phase') }" data-testid="cell-detail-phase-field">
              <label class="tc-attribution-label">Phase</label>
              <!-- An empty combobox would read as "this project has no phases"
                   when VP simply could not be asked (#6238). -->
              <div v-if="phasesUnknown" class="small text-danger d-flex align-items-center gap-2" data-testid="cell-detail-phase-unknown">
                Couldn't load phases
                <button type="button" class="btn btn-link btn-sm p-0" data-testid="cell-detail-phase-retry" @click="refetchPhases()">Retry</button>
              </div>
              <SearchCombobox
                v-else
                testid="cell-detail-phase"
                placeholder="Select phase"
                recent-label=""
                empty-label="No phases available."
                :model-value="phaseModel"
                :options="phaseComboOptions"
                :loading="phasesLoading"
                :disabled="regenerating"
                @update:query="phaseQuery = $event"
                @select="onPhaseSelect"
              />
            </div>
            <div
              v-if="selectedPhase && (phaseHasTasks || selectedTask || tasksUnknown)"
              class="tc-attribution-field"
              :class="{ 'border border-danger rounded p-2': flagged('task') }"
              data-testid="cell-detail-task-field"
            >
              <label class="tc-attribution-label">Task</label>
              <!-- VP could not be asked for the task list. The combobox is left
                   out entirely rather than shown empty, which would read as
                   "this phase has no tasks" (#6238). -->
              <div v-if="tasksUnknown" class="small text-danger d-flex align-items-center gap-2" data-testid="cell-detail-task-unknown">
                Couldn't load tasks
                <button type="button" class="btn btn-link btn-sm p-0" data-testid="cell-detail-task-retry" @click="refetchTasks()">Retry</button>
              </div>
              <SearchCombobox
                v-else
                testid="cell-detail-task"
                placeholder="Select task"
                recent-label=""
                empty-label="No tasks available."
                :model-value="taskModel"
                :options="taskComboOptions"
                :loading="tasksLoading"
                :disabled="regenerating"
                @update:query="taskQuery = $event"
                @select="onTaskSelect"
              />
            </div>
          </template>

          <!-- Hours — cell total, stepped by the tenant's VP increment. Edits
               scale the cell's contributing events proportionally (cell-only
               scope — edit standard rule 3). -->
          <div class="tc-attribution-field" :class="{ 'border border-danger rounded p-2': flagged('hours') }" data-testid="cell-detail-hours-field">
            <label class="tc-attribution-label">Hours</label>
            <div class="tc-hours-stepper" data-testid="cell-detail-hours-stepper">
              <button
                type="button"
                class="tc-btn-stepper"
                aria-label="Decrease hours"
                data-testid="cell-detail-hours-decrement"
                :disabled="regenerating"
                @click="stepHours(-1)"
              >−</button>
              <input
                type="number"
                class="form-control form-control-sm tc-hours-stepper-input"
                :step="hoursIncrement"
                :value="hoursValue"
                :disabled="regenerating"
                data-testid="cell-detail-hours-input"
                @change="onHoursInput"
                @blur="commitHours"
              />
              <button
                type="button"
                class="tc-btn-stepper"
                aria-label="Increase hours"
                data-testid="cell-detail-hours-increment"
                :disabled="regenerating"
                @click="stepHours(1)"
              >+</button>
            </div>
          </div>

          <!-- Note — the comment that gets written to VP. Directly editable;
               Save enables once the text differs from the stored note. -->
          <div class="tc-attribution-field tc-cell-detail-note-field mb-0">
            <label class="tc-attribution-label">Comment<template v-if="commentRequired"><span class="text-danger fw-bold" aria-hidden="true"> *</span><span class="visually-hidden"> (required)</span></template></label>
            <textarea
              class="form-control tc-cell-detail-note-input"
              :class="{ 'is-invalid': flagged('comment') }"
              rows="3"
              placeholder="Add a comment"
              data-testid="cell-detail-note-input"
              :disabled="regenerating"
              v-model="draftText"
            ></textarea>
            <div class="tc-cell-detail-note-actions">
              <span v-if="note && draftText === note" class="tc-cell-detail-note-source">{{ noteAttribution }}</span>
              <div class="flex-fill"></div>
              <button
                v-if="events.length > 0"
                type="button"
                class="btn btn-outline-secondary btn-sm"
                data-testid="cell-detail-regenerate"
                :disabled="regenerating"
                @click="emit('regenerate')"
              ><span v-if="regenerating" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>{{ regenerating ? 'Regenerating…' : 'Regenerate' }}</button>
            </div>
            <p
              v-if="regenerateError"
              class="text-danger small mt-2 mb-0"
              role="alert"
              data-testid="cell-detail-note-error"
            >{{ regenerateError }}</p>
          </div>

          <!-- Detail — reasoning + source events, collapsed by default -->
          <div v-if="hasReasoning || events.length > 0" class="tc-cell-detail-why">
            <div
              class="tc-cell-detail-disclosure tc-cell-detail-why-toggle"
              role="button"
              tabindex="0"
              :aria-expanded="whyOpen"
              data-testid="cell-detail-why-toggle"
              @click="whyOpen = !whyOpen"
              @keydown.enter="whyOpen = !whyOpen"
              @keydown.space.prevent="whyOpen = !whyOpen"
            >
              <span class="tc-cell-detail-chevron">{{ whyOpen ? '▴' : '▾' }}</span>
              <span class="tc-cell-detail-why-label">Detail</span>
              <span v-if="events.length > 0" class="tc-cell-detail-why-count">· {{ events.length }} {{ events.length === 1 ? 'event' : 'events' }}</span>
            </div>

            <div v-if="whyOpen" class="tc-cell-detail-why-body" data-testid="cell-detail-why">
              <div v-if="attributionBadge || hasReasoning" class="tc-cell-popover-reasoning tc-cell-detail-reasoning">
                <template v-if="attributionBadge || reasoning">
                  <div class="tc-cell-popover-reasoning-heading">Picked because</div>
                  <div class="tc-cell-detail-picked">
                    <span
                      v-if="attributionBadge"
                      class="tc-cell-detail-attr-chip"
                      :class="`tc-cell-detail-attr-chip--${attributionBadge.tone}`"
                      data-testid="cell-detail-attribution"
                    >{{ attributionBadge.label }}</span>
                    <span v-if="reasoning" class="tc-cell-detail-picked-reason">{{ reasoning }}</span>
                  </div>
                </template>
                <template v-if="lessSure.length > 0">
                  <div class="tc-cell-popover-reasoning-heading">Less sure about</div>
                  <ul class="tc-cell-popover-reasoning-bullets">
                    <li v-for="(s, i) in lessSure" :key="i">{{ s }}</li>
                  </ul>
                </template>
              </div>

              <div v-if="events.length > 0" class="tc-cell-detail-events-section">
                <div class="tc-cell-popover-reasoning-heading">From {{ events.length }} {{ events.length === 1 ? 'event' : 'events' }} · {{ eventsTotal.toFixed(2) }}h</div>
                <ul class="tc-cell-detail-events" data-testid="cell-detail-events">
                  <li v-for="ev in events" :key="ev.eventId" class="tc-cell-detail-event">
                    <div class="tc-cell-detail-event-head">
                      <SourceBadge v-if="ev.source" :source="ev.source" variant="compact" />
                      <span v-if="ev.subject" class="tc-cell-detail-event-title">{{ ev.subject }}</span>
                      <span v-else class="tc-cell-detail-event-title tc-cell-detail-event-title--missing">Event details unavailable</span>
                      <SourceLink :link="ev.link ?? null" />
                      <span class="tc-cell-detail-event-hours">{{ ev.hours.toFixed(2) }}h</span>
                    </div>
                    <div v-if="ev.start" class="tc-cell-detail-event-when">{{ formatWhen(ev.start) }}</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer — Save commits every staged edit to the cell; Cancel discards. -->
        <div class="tc-attribution-action-bar">
          <button
            v-if="!isNew"
            type="button"
            class="btn btn-outline-danger btn-sm"
            data-testid="cell-detail-remove"
            :disabled="regenerating"
            @click="emit('remove')"
          >Remove entry</button>

          <!-- Copy from — another day of this row, hours and comment together.
               A pick writes the cell outright; nothing here is staged. -->
          <div
            v-if="copySources.length > 0"
            ref="copyRef"
            class="dropdown"
            :class="{ show: copyOpen }"
          >
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm dropdown-toggle"
              aria-haspopup="true"
              :aria-expanded="copyOpen"
              data-testid="cell-detail-copy-from"
              :disabled="regenerating"
              @click="copyOpen = !copyOpen"
            >Copy from</button>
            <ul
              class="dropdown-menu tc-cell-copy-menu"
              :class="{ show: copyOpen }"
              data-testid="cell-detail-copy-menu"
            >
              <li v-for="src in copySources" :key="src.dayIndex">
                <button
                  class="dropdown-item tc-cell-copy-item"
                  type="button"
                  :data-testid="`cell-detail-copy-day-${src.dayIndex}`"
                  @click="onCopyPick(src.dayIndex)"
                >
                  <span class="tc-cell-copy-item-top">
                    <span>{{ src.label }}</span>
                    <span class="tc-cell-copy-item-hours">{{ src.hours.toFixed(2) }}h</span>
                  </span>
                  <span v-if="src.note" class="tc-cell-copy-item-note">{{ src.note }}</span>
                </button>
              </li>
            </ul>
          </div>

          <div class="tc-attribution-action-spacer"></div>
          <span
            v-if="blockerShown && blocker"
            class="text-danger small me-auto"
            role="alert"
            data-testid="cell-detail-blocker"
          >{{ blocker.message }}</span>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            data-testid="cell-detail-cancel"
            :disabled="regenerating"
            @click="emit('close')"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            data-testid="cell-detail-save"
            :disabled="regenerating"
            @click="onSave"
          >Save</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import SearchCombobox from './SearchCombobox.vue';
import type { ComboOption } from './SearchCombobox.vue';
import SourceBadge from './ui/SourceBadge.vue';
import SourceLink from './ui/SourceLink.vue';
import type { SourceEventRef } from './sourceFacts';
import type { CopySource } from '../composables/useTriageActions';
import { usePhaseCascade } from '../composables/usePhaseCascade';
import { useProjectSearch } from '../composables/useProjectSearch';
import { isUncertain } from '../theme/confidence';
import { sameTarget, type BillingTarget, type RowConfidence } from '../api/draft';
import type { TimesheetRow } from '../api/client';

const props = defineProps<{
  open: boolean;
  target: BillingTarget | null;
  dayLabel: string;
  hours: number;
  note: string;
  noteKind: string;
  reasoning: string;
  // How the target was chosen — 'rule'|'memory'|'agent'|'saved'|'manual'.
  // Empty when unknown (no chip rendered).
  attributionSource?: string;
  lessSure: string[];
  events: SourceEventRef[];
  regenerating: boolean;
  // Set when the last Regenerate failed or produced no note, so the user gets
  // feedback instead of the action appearing to do nothing.
  regenerateError: string;
  // Passed from the row's DraftRow.isPhased — drives the phase/task pickers.
  isPhased?: boolean;
  /** Hours step from tenant settings. Defaults to 0.25. */
  hoursIncrement?: number;
  // VP timesheet rows for the active period — narrows phase candidates to the
  // employee's on-timesheet leaves (ADR-0028 amendment 2026-06-16).
  timesheetRows?: TimesheetRow[];
  // VP RequireComments ≠ 'N' — clearing the note is blocked (VP would reject
  // the noteless entry at save).
  commentRequired?: boolean;
  // In-play targets + memory ranking for the shared ProjectField list.
  recent?: BillingTarget[];
  rankedTargetIds?: string[];
  // The tenant's VP time categories (ADR-0044) — offered as picks so a cell can
  // be re-targeted to a category.
  categories?: BillingTarget[];
  // Row-level confidence + confirmed state. When the cell is uncertain and not
  // yet confirmed the card leads with a plain-words confidence cue and reveals
  // the "why"; confirming the row clears both.
  confidence?: RowConfidence;
  confirmed?: boolean;
  // The cell holds nothing yet — the card is putting hours on an untouched day,
  // so there is no entry to remove.
  isNew?: boolean;
  // The row's other days, offered under Copy from.
  copySources?: CopySource[];
}>();

const copySources = computed(() => props.copySources ?? []);

const emit = defineEmits<{
  close: [];
  regenerate: [];
  remove: [];
  // The card stages every edit locally; Save commits project/phase/task, hours
  // and note to the cell in one write.
  save: [payload: { target: BillingTarget; hours: number; note: string; noteKind: string }];
  // Copy another day of this row onto this cell — written by the host, not staged.
  copyFrom: [dayIndex: number];
}>();

const whyOpen = ref(false);
const draftText = ref('');

// Copy-from menu — manual open state, as the Regenerate dropdown does; Bootstrap
// JS is not bundled in this app.
const copyRef = ref<HTMLElement | null>(null);
const copyOpen = ref(false);

function onCopyOutsideClick(e: MouseEvent): void {
  if (copyRef.value && !copyRef.value.contains(e.target as Node)) copyOpen.value = false;
}

watch(copyOpen, (isOpen) => {
  if (isOpen) document.addEventListener('mousedown', onCopyOutsideClick);
  else document.removeEventListener('mousedown', onCopyOutsideClick);
});

function onCopyPick(dayIndex: number): void {
  copyOpen.value = false;
  emit('copyFrom', dayIndex);
}

const hasReasoning = computed(() => !!props.reasoning || props.lessSure.length > 0);

// The agent flagged this cell for review and the user hasn't confirmed it yet —
// lead with the plain-words confidence and reveal the "why" (same test as the
// grid's amber corner flag, so the card explains exactly the flagged cells).
const showConfidenceCue = computed(() =>
  !!props.confidence && !props.confirmed && isUncertain(props.confidence),
);
const eventsTotal = computed((): number => props.events.reduce((sum, e) => sum + e.hours, 0));

// Provenance caption beneath the note — friendlier than the raw kind token.
const noteAttribution = computed((): string => {
  switch (props.noteKind) {
    case 'edited': return 'Edited by you';
    case 'user': return 'Written by you';
    default: return 'Drafted by agent';
  }
});

// Attribution chip — how this cell's target was chosen (ADR-0029 amendment §"Detail").
const attributionBadge = computed((): { label: string; tone: 'auto' | 'manual'; mechanism: string } | null => {
  switch (props.attributionSource) {
    case 'manual': return { label: 'Manual', tone: 'manual', mechanism: 'you set this project' };
    case 'rule':   return { label: 'Auto',   tone: 'auto',   mechanism: 'keyword rule' };
    case 'memory': return { label: 'Auto',   tone: 'auto',   mechanism: 'your past timesheets' };
    case 'agent':  return { label: 'Auto',   tone: 'auto',   mechanism: 'the agent' };
    case 'saved':  return { label: 'Auto',   tone: 'auto',   mechanism: 'your saved draft' };
    default:       return null;
  }
});

// ── Staged edits (cell-scoped) ───────────────────────────────────────────────
// The card holds every edit locally — project, phase, task, hours, note — and
// writes them to the cell (this cell's events only) in one go on Save. Cancel
// or close discards. Nothing touches the draft while the user is editing.
const stagedProject = ref<{ wbs1: string; wbs1Name: string; category?: string }>({ wbs1: '', wbs1Name: '' });

// pickedPhased is seeded from the row's isPhased on open, then tracks each
// in-card project pick — so changing to a non-phased project correctly hides
// the phase fields even if the original row was phased.
const pickedPhased = ref(false);
// pickedPhased is authoritative for the row's own target (the server resolved
// it), but an option the user picks carries only a guess — the option list
// infers isPhased because BillingTarget has no such field. So VP's answer counts
// too: whichever says phased wins (#6487, same as AttributionWizard).
const showPhase = computed(() =>
  !!stagedProject.value.wbs1 && (pickedPhased.value || phaseCandidates.value.length > 0));

const {
  selectedPhase,
  selectedTask,
  phaseCandidates,
  taskCandidates,
  phasesLoading,
  tasksLoading,
  phasesUnknown,
  tasksUnknown,
  refetchPhases,
  refetchTasks,
  phaseHasTasks,
  leafChosen,
  onPhaseChange,
  reset: resetCascade,
} = usePhaseCascade({
  wbs1: () => stagedProject.value.wbs1,
  timesheetRows: () => props.timesheetRows ?? [],
  enabled: () => props.open && !!stagedProject.value.wbs1 && !stagedProject.value.category,
});

// The staged leaf must stay in the option lists even when the candidate lists
// (on-sheet narrowing / VP fetch) don't carry it yet.
const phaseOptions = computed(() => {
  const sel = selectedPhase.value;
  if (sel && !phaseCandidates.value.some(p => p.wbs2 === sel.wbs2)) {
    return [sel, ...phaseCandidates.value];
  }
  return phaseCandidates.value;
});

const taskOptions = computed(() => {
  const sel = selectedTask.value;
  if (sel && !taskCandidates.value.some(x => x.wbs3 === sel.wbs3)) {
    return [sel, ...taskCandidates.value];
  }
  return taskCandidates.value;
});

// ── Combobox fields (project / phase / task) ────────────────────────────────
// One SearchCombobox per field. Project search + recents come from the shared
// composable; phase/task filter their cascade candidates locally.
const {
  options: projectResults,
  loading: projectLoading,
  emptyLabel: projectEmpty,
  setQuery: setProjectQuery,
  resolve: resolveProject,
  reset: resetProjectSearch,
} = useProjectSearch({
  recent: () => props.recent ?? [],
  rankedTargetIds: () => props.rankedTargetIds ?? [],
  enabled: () => props.open,
  timesheetRows: () => props.timesheetRows ?? [],
  categories: () => props.categories ?? [],
});

const projectModel = computed((): ComboOption | null => {
  const p = stagedProject.value;
  if (!p.wbs1) return null;
  return p.category
    ? { id: `cat:${p.category}`, name: p.wbs1Name || p.wbs1 }
    : { id: p.wbs1, code: p.wbs1, name: p.wbs1Name };
});

function onProjectSelect(opt: ComboOption): void {
  const p = resolveProject(opt.id);
  if (!p) return;
  pickedPhased.value = p.isPhased;
  stagedProject.value = { wbs1: p.wbs1, wbs1Name: p.wbs1Name, category: p.category };
  // Clear the old leaf; the cascade auto-picks the new project's sole leaf once
  // its phases load (its own phaseCandidates watch, guarded on an empty phase).
  phaseQuery.value = '';
  taskQuery.value = '';
  selectedPhase.value = null;
  selectedTask.value = null;
}

const phaseQuery = ref('');
const taskQuery = ref('');
function matchOption(q: string, code: string, name: string): boolean {
  const s = q.trim().toLowerCase();
  return !s || code.toLowerCase().includes(s) || name.toLowerCase().includes(s);
}

const phaseComboOptions = computed((): ComboOption[] =>
  phaseOptions.value
    .filter((p) => matchOption(phaseQuery.value, p.wbs2, p.name))
    .map((p) => ({ id: p.wbs2, code: p.wbs2, name: p.name })),
);
const phaseModel = computed((): ComboOption | null =>
  selectedPhase.value ? { id: selectedPhase.value.wbs2, code: selectedPhase.value.wbs2, name: selectedPhase.value.name } : null,
);
function onPhaseSelect(opt: ComboOption): void {
  onPhaseChange(phaseOptions.value.find((p) => p.wbs2 === opt.id) ?? null);
  phaseQuery.value = '';
}

const taskComboOptions = computed((): ComboOption[] =>
  taskOptions.value
    .filter((t) => matchOption(taskQuery.value, t.wbs3, t.name))
    .map((t) => ({ id: t.wbs3, code: t.wbs3, name: t.name })),
);
const taskModel = computed((): ComboOption | null =>
  selectedTask.value ? { id: selectedTask.value.wbs3, code: selectedTask.value.wbs3, name: selectedTask.value.name } : null,
);
function onTaskSelect(opt: ComboOption): void {
  selectedTask.value = taskOptions.value.find((t) => t.wbs3 === opt.id) ?? null;
  taskQuery.value = '';
}

// The staged target: the picked project plus whatever leaf the cascade holds.
const stagedTarget = computed((): BillingTarget => ({
  wbs1: stagedProject.value.wbs1,
  wbs1Name: stagedProject.value.wbs1Name,
  wbs2: selectedPhase.value?.wbs2 ?? '',
  wbs2Name: selectedPhase.value?.name ?? '',
  wbs3: selectedTask.value?.wbs3 ?? '',
  wbs3Name: selectedTask.value?.name ?? '',
  ...(stagedProject.value.category ? { category: stagedProject.value.category } : {}),
}));

// A pick that changes only the category is a real edit — sameTarget counts it,
// where comparing the WBS path alone would read the swap as no change and leave
// Save disabled with no way to correct the cell.

// A phased project needs its leaf resolved before it can be saved; a flat one
// (or a phased one with no phases) is complete at the WBS1.
const targetComplete = computed((): boolean => {
  if (!stagedProject.value.wbs1) return false;
  if (!pickedPhased.value && phaseCandidates.value.length === 0 && !phasesLoading.value) return true;
  if (phasesLoading.value) return false;
  if (phaseCandidates.value.length === 0) return true;
  return leafChosen.value;
});

// The note baseline is captured at open, not read from props.note — Regenerate
// updates props.note to fill the staged field, so comparing against it would
// hide the change. target/hours aren't mutated while open, so props is fine.
const initialNote = ref('');
const dirty = computed((): boolean =>
  !sameTarget(props.target, stagedTarget.value) ||
  hoursValue.value !== props.hours ||
  draftText.value !== initialNote.value,
);

// What Save is still waiting on — the field and what to do about it — in the
// order the card reads, or null when nothing is. Save stays clickable and says
// this on click: a form that shouts before it has been filled in, or a dead
// button with no reason, both leave the user hunting.
// Hours are what makes a cell exist: a new one starts at 0 and can't be saved
// until it carries time, and an existing one can't be emptied — that's Remove.
const blocker = computed((): { field: string; message: string } | null => {
  if (!stagedProject.value.wbs1) return { field: 'project', message: 'Pick a project.' };
  if (phasesLoading.value) return { field: 'phase', message: 'Still loading phases.' };
  if (!targetComplete.value) {
    return selectedPhase.value
      ? { field: 'task', message: 'Pick a task.' }
      : { field: 'phase', message: 'Pick a phase.' };
  }
  if (!(hoursValue.value > 0)) return { field: 'hours', message: 'Enter hours above zero.' };
  if (props.commentRequired && !draftText.value.trim()) {
    return { field: 'comment', message: 'Add a comment.' };
  }
  if (!dirty.value) return { field: '', message: 'Nothing changed yet.' };
  return null;
});

// Set by a Save that couldn't go through, so the reason appears on the attempt
// rather than nagging a card the user is still filling in.
const blockerShown = ref(false);
const flagged = (field: string): boolean => blockerShown.value && blocker.value?.field === field;

function onSave(): void {
  if (blocker.value) {
    blockerShown.value = true;
    return;
  }
  emit('save', {
    target: stagedTarget.value,
    hours: hoursValue.value,
    note: draftText.value,
    noteKind: props.noteKind,
  });
}

// ── Hours stepper — free typing until blur, which snaps to the increment and
// commits. Min one increment: emptying a cell is "Remove entry", not 0h.
const hoursValue = ref(0);

const increment = computed(() => props.hoursIncrement || 0.25);

function snapHours(value: number): number {
  const inc = increment.value;
  const snapped = Math.max(inc, Math.round(value / inc) * inc);
  return Math.round(snapped * 100) / 100;
}

function onHoursInput(e: Event): void {
  const v = Number((e.target as HTMLInputElement).value);
  hoursValue.value = Number.isFinite(v) ? v : hoursValue.value;
}

function commitHours(): void {
  hoursValue.value = snapHours(hoursValue.value);
}

function stepHours(direction: 1 | -1): void {
  hoursValue.value = snapHours(hoursValue.value + direction * increment.value);
}

// Seed the staged edits from the cell each time the modal opens.
watch(() => props.open, (isOpen) => {
  if (!isOpen) return;
  // Uncertain cells open with the "why" already revealed (progressive disclosure
  // of the flag cue); everything else stays collapsed.
  whyOpen.value = showConfidenceCue.value;
  copyOpen.value = false;
  blockerShown.value = false;
  pickedPhased.value = !!props.isPhased && !props.target?.category;
  stagedProject.value = props.target
    ? { wbs1: props.target.wbs1, wbs1Name: props.target.wbs1Name, category: props.target.category }
    : { wbs1: '', wbs1Name: '' };
  hoursValue.value = props.hours;
  draftText.value = props.note;
  initialNote.value = props.note;
  resetProjectSearch();
  phaseQuery.value = '';
  taskQuery.value = '';
  resetCascade(
    props.target?.wbs2
      ? { wbs2: props.target.wbs2, name: props.target.wbs2Name, chargeType: '', status: 'A' }
      : null,
    props.target?.wbs3
      ? { wbs3: props.target.wbs3, name: props.target.wbs3Name, chargeType: '', status: 'A' }
      : null,
  );
}, { immediate: true });

// Regenerate updates the stored note prop; mirror it into the staged draft.
watch(() => props.note, (n) => { draftText.value = n; });

// Escape shuts the Copy-from menu if it is open, otherwise the card (discarding
// staged edits). Because the drawer suppresses Escape while this is open, it
// never leaks to the Triage drawer.
function onEscape(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || !props.open) return;
  if (copyOpen.value) copyOpen.value = false;
  else emit('close');
}
onMounted(() => document.addEventListener('keydown', onEscape));
onUnmounted(() => {
  document.removeEventListener('keydown', onEscape);
  document.removeEventListener('mousedown', onCopyOutsideClick);
});

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
</script>
